import { makeAutoObservable, runInAction } from 'mobx'
import { toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { logger } from 'shared/lib'
import { SurveysApi } from 'entities/Surveys'
import { SurveysStatus } from 'entities/Surveys/api/types'
import type { Contact } from 'entities/Contacts/model/Contact'
import type { IResponseFilterSegment } from 'entities/Segment'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SurveySendModal } from 'widgets/SurveySend/ui/SurveySendModal'

export class SurveySendStore {
  private _isActive = false
  private _isChecked = false
  private _modalId = 'surveySendModalId'

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get label() {
    return 'Send Survey'
  }

  get isActive() {
    return this._isActive
  }

  get isChecked() {
    return this._isChecked
  }

  getValidContactIds = async (contactIds: number[]) => {
    try {
      const contacts = (await this._entities.contactsStore.getByIds(contactIds)) ?? []
      const blockReason = this.getBlockReason(...contacts)

      if (blockReason) {
        toastStore.add({
          type: 'error',
          title: blockReason,
        })

        return null
      }

      return contacts
        .filter((contact) => !contact.isOptOut && !contact.isBlocked)
        .map((contact) => contact.id)
    } catch (error) {
      logger.error(error)

      toastStore.add({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })

      return null
    }
  }

  getBlockReason = (...contacts: Contact[]) => {
    switch (true) {
      case this._isChecked && !this._isActive:
        return 'No active surveys'
      case !contacts.length:
        return 'No selected contact'
      case contacts.length === 1 && contacts[0].isOptOut:
        return 'This contact has opted-out'
      case contacts.length === 1 && contacts[0].isBlocked:
        return 'This contact has been blocked'
      case contacts.every(({ isOptOut, isBlocked }) => isOptOut || isBlocked):
        return 'Contacts has opted-out or has been blocked'
      default:
        return null
    }
  }

  checkActivity = () => {
    SurveysApi.getSurveys({
      page: 1,
      limit: 1,
      'filters[status]': SurveysStatus.Active,
    }).then(
      ({ data }) =>
        runInAction(() => {
          this._isActive = !!data.meta?.total
          this._isChecked = true
        }),
      () =>
        runInAction(() => {
          this._isActive = false
          this._isChecked = true
        })
    )
  }

  enrollContacts = (contactIds: number[]) => {
    if (this._isChecked && !this._isActive) {
      toastStore.add({ type: 'error', title: 'No active surveys' })
      return
    }
    this.showModal({ contactIds })
  }

  enrollContactsByFilters = (
    filtersList?: IResponseFilterSegment[],
    search?: string,
    bulkAll?: boolean
  ) => {
    if (this._isChecked && !this._isActive) {
      toastStore.add({ type: 'error', title: 'No active surveys' })
      return
    }
    this.showModal({ filtersList, search, bulkAll })
  }

  showModal = ({
    contactIds,
    filtersList,
    search,
    bulkAll,
  }: {
    contactIds?: number[]
    filtersList?: IResponseFilterSegment[]
    search?: string
    bulkAll?: boolean
  }) => {
    const onClose = () => modalStore.removeModal(this._modalId)

    modalStore.addModal({
      id: this._modalId,
      title: this.label,
      width: 480,
      showCloseButton: false,
      showCloseIcon: true,
      paddingContent: '12px 24px 24px',
      ModalContent: SurveySendModal,
      ModalContentProps: { contactIds, filtersList, search, bulkAll, onClose },
      onClose: onClose,
    })
  }
}
